import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Eye, EyeOff, Check, X } from "lucide-react";

const RULES = [
  { key: "length", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { key: "upper", label: "One uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter (a-z)", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One digit (0-9)", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
  { key: "nospace", label: "No spaces", test: (p) => p.length > 0 && !/\s/.test(p) },
];

const inputCls = "w-full h-10 px-3 pr-10 border border-slate-300 rounded-sm text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none bg-white";

function PasswordField({ label, value, onChange, testId, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-slate-600">{label}</span>
      <div className="mt-1.5 relative">
        <input
          data-testid={testId}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          autoComplete="new-password"
          className={inputCls}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-2 p-1 text-slate-500 hover:text-slate-900"
          aria-label={show ? "Hide password" : "Show password"}
          tabIndex={-1}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </label>
  );
}

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const rulesState = RULES.map((r) => ({ ...r, ok: r.test(newPw) }));
  const allValid = rulesState.every((r) => r.ok);
  const match = newPw.length > 0 && newPw === confirmPw;

  const reset = () => {
    setCurrentPw(""); setNewPw(""); setConfirmPw(""); setError(""); setSaving(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allValid) return setError("New password does not meet all requirements.");
    if (!match) return setError("New password and confirmation do not match.");
    if (currentPw === newPw) return setError("New password must be different from the current password.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password updated. Please sign in again with your new password.");
      reset();
      onOpenChange(false);
      // Force re-login for safety
      setTimeout(() => {
        localStorage.removeItem("ns_token");
        window.location.href = "/admin/login";
      }, 900);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <KeyRound size={18} /> Change Password
          </DialogTitle>
          <DialogDescription>
            Use a strong, unique password. You'll be signed out and prompted to sign in with the new password.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <PasswordField
            label="Current Password"
            value={currentPw}
            onChange={setCurrentPw}
            testId="cp-current"
            autoFocus
          />
          <PasswordField
            label="New Password"
            value={newPw}
            onChange={setNewPw}
            testId="cp-new"
          />
          <ul className="space-y-1 pl-1">
            {rulesState.map((r) => (
              <li key={r.key} className={`flex items-center gap-1.5 text-[11px] ${r.ok ? "text-emerald-700" : "text-slate-500"}`} data-testid={`rule-${r.key}`}>
                {r.ok ? <Check size={12} /> : <X size={12} />}
                {r.label}
              </li>
            ))}
          </ul>
          <PasswordField
            label="Confirm New Password"
            value={confirmPw}
            onChange={setConfirmPw}
            testId="cp-confirm"
          />
          {confirmPw.length > 0 && (
            <div className={`text-[11px] flex items-center gap-1.5 ${match ? "text-emerald-700" : "text-red-600"}`} data-testid="cp-match">
              {match ? <Check size={12} /> : <X size={12} />}
              {match ? "Passwords match" : "Passwords do not match"}
            </div>
          )}

          {error && (
            <div className="p-2.5 border border-red-200 bg-red-50 text-red-800 text-xs rounded-sm" data-testid="cp-error">
              {error}
            </div>
          )}

          <DialogFooter>
            <button
              type="button"
              onClick={() => { reset(); onOpenChange(false); }}
              className="h-9 px-3 border border-slate-300 rounded-sm text-sm hover:bg-slate-100"
              data-testid="cp-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !allValid || !match || !currentPw}
              data-testid="cp-submit"
              className="h-9 px-4 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            >
              <KeyRound size={14} />
              {saving ? "Updating…" : "Update Password"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
